import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  NEUTRAL,
  RemoteSender,
  SE_SPEED,
  SLIDER_STEP,
  SU_SLIDER_DEFAULT,
  clampSlider,
  fromGamepad,
  fromPressedKeys,
  keyAction,
  mergeSources,
  padEdges,
} from "../lib/input";
import type { SeSpeed } from "../lib/input";
import type {
  TouchControl,
  Dpad,
  PadSnapshot,
  RemoteInput,
  RemoteVariant,
  SenderStats,
  SourceState,
} from "../types";

export interface UseRemoteOptions {
  variant: RemoteVariant;
  enabled: boolean;
  maxHz: number;
  send: (s: RemoteInput) => void;
  onReset?: () => void;
  onStop?: () => void;
  /** 키보드를 받을 대상(기본 window) */
  keyTarget?: Window | null;
}

export interface RemoteApi {
  /** 지금 합친 입력(보낸 값과 같거나 곧 보낼 값) */
  current: RemoteInput;
  slider: number;
  setSlider: (v: number) => void;
  seSpeed: SeSpeed;
  setSeSpeed: (s: SeSpeed) => void;
  press: (pointerId: number, c: TouchControl) => void;
  release: (pointerId: number) => void;
  /** 눌린 컨트롤(화면 표시용 — 모든 출처) */
  held: ReadonlySet<TouchControl>;
  gamepad: boolean;
  stats: SenderStats;
}

const EMPTY: SourceState = { dpad: null, a: false, b: false };

/**
 * FS-09 리모컨 입력 훅 — 터치(포인터 · 멀티터치) · 키보드 · 게임패드를 모아 바뀔 때만 maxHz 로 보낸다.
 * 화면(FS-04-02)은 `send` 로 커널 호스트 input() 을 준다. R/Esc(게임패드 BACK/START)는 onReset/onStop.
 */
export function useRemote(o: UseRemoteOptions): RemoteApi {
  const { variant, enabled, maxHz } = o;
  const sendRef = useRef(o.send);
  sendRef.current = o.send;
  const onResetRef = useRef(o.onReset);
  onResetRef.current = o.onReset;
  const onStopRef = useRef(o.onStop);
  onStopRef.current = o.onStop;

  const sender = useMemo(() => new RemoteSender((s) => sendRef.current(s), maxHz), []); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => sender.setMaxHz(maxHz), [sender, maxHz]);
  useEffect(() => () => sender.dispose(), [sender]);

  const [slider, setSliderState] = useState(SU_SLIDER_DEFAULT);
  const [seSpeed, setSeSpeed] = useState<SeSpeed>("fast");
  const [touch, setTouch] = useState<Map<number, TouchControl>>(() => new Map());
  const [keys, setKeys] = useState<SourceState>(EMPTY);
  const [pad, setPad] = useState<SourceState>(EMPTY);
  const [gamepad, setGamepad] = useState(false);
  const lastAxis = useRef<"x" | "y">("y");

  const setSlider = useCallback((v: number) => setSliderState(clampSlider(v)), []);

  // ── 터치 ──
  const touchState = useMemo<SourceState>(() => {
    let dpad: Dpad = null;
    let a = false;
    let b = false;
    for (const c of touch.values()) {
      if (c === "a") a = true;
      else if (c === "b") b = true;
      else dpad = c; // 마지막으로 누른 방향
    }
    return { dpad, a, b };
  }, [touch]);

  const press = useCallback((id: number, c: TouchControl) => {
    if (c !== "a" && c !== "b") lastAxis.current = c === "e" || c === "w" ? "x" : "y";
    setTouch((m) => {
      const n = new Map(m);
      n.delete(id); // 같은 손가락이 다른 버튼으로 → 새 것만
      n.set(id, c);
      return n;
    });
  }, []);
  const release = useCallback((id: number) => {
    setTouch((m) => {
      if (!m.has(id)) return m;
      const n = new Map(m);
      n.delete(id);
      return n;
    });
  }, []);

  // ── 키보드 ──
  useEffect(() => {
    if (!enabled) {
      setKeys(EMPTY);
      return;
    }
    const target = o.keyTarget === undefined ? window : o.keyTarget;
    if (!target) return;
    const down = new Set<string>();
    const sync = () => setKeys(fromPressedKeys(down));
    const onDown = (e: KeyboardEvent) => {
      if (e.altKey || e.ctrlKey || e.metaKey) return;
      const act = keyAction(e.code);
      if (!act) return;
      e.preventDefault();
      if (e.repeat) return;
      if (act.kind === "dir") {
        lastAxis.current = act.axis;
        down.add(e.code);
        sync();
      } else if (act.kind === "button") {
        down.add(e.code);
        sync();
      } else if (act.kind === "slider") {
        if (variant === "se") setSeSpeed(act.delta > 0 ? "fast" : "slow");
        else setSliderState((v) => clampSlider(v + act.delta * SLIDER_STEP));
      } else if (act.kind === "reset") onResetRef.current?.();
      else if (act.kind === "stop") onStopRef.current?.();
    };
    const onUp = (e: KeyboardEvent) => {
      if (down.delete(e.code)) sync();
    };
    const onBlur = () => {
      down.clear();
      sync();
    };
    target.addEventListener("keydown", onDown as EventListener);
    target.addEventListener("keyup", onUp as EventListener);
    window.addEventListener("blur", onBlur);
    return () => {
      target.removeEventListener("keydown", onDown as EventListener);
      target.removeEventListener("keyup", onUp as EventListener);
      window.removeEventListener("blur", onBlur);
      down.clear();
    };
  }, [enabled, variant, o.keyTarget]);

  // ── 게임패드(FS-09-01 상태 '게임패드 연결') ──
  useEffect(() => {
    if (typeof navigator === "undefined" || typeof navigator.getGamepads !== "function") return;
    const count = () => [...navigator.getGamepads()].filter(Boolean).length;
    const update = () => setGamepad(count() > 0);
    update();
    window.addEventListener("gamepadconnected", update);
    window.addEventListener("gamepaddisconnected", update);
    return () => {
      window.removeEventListener("gamepadconnected", update);
      window.removeEventListener("gamepaddisconnected", update);
    };
  }, []);

  useEffect(() => {
    if (!enabled || !gamepad) {
      setPad(EMPTY);
      return;
    }
    let raf = 0;
    let prev: PadSnapshot | null = null;
    let last = EMPTY;
    const poll = () => {
      const p = [...navigator.getGamepads()].find(Boolean) ?? null;
      if (p) {
        const snap: PadSnapshot = {
          axes: [...p.axes],
          buttons: p.buttons.map((b) => ({ pressed: b.pressed })),
        };
        const s = fromGamepad(snap);
        if (s.dpad !== last.dpad || s.a !== last.a || s.b !== last.b) {
          if (s.dpad && s.dpad.length === 1)
            lastAxis.current = s.dpad === "e" || s.dpad === "w" ? "x" : "y";
          last = s;
          setPad(s);
        }
        const ed = padEdges(prev, snap);
        if (ed.sliderUp || ed.sliderDown) {
          const d = ed.sliderUp ? 1 : -1;
          if (variant === "se") setSeSpeed(d > 0 ? "fast" : "slow");
          else setSliderState((v) => clampSlider(v + d * SLIDER_STEP));
        }
        if (ed.reset) onResetRef.current?.();
        if (ed.stop) onStopRef.current?.();
        prev = snap;
      }
      raf = requestAnimationFrame(poll);
    };
    raf = requestAnimationFrame(poll);
    return () => cancelAnimationFrame(raf);
  }, [enabled, gamepad, variant]);

  // ── 합치기 · 보내기 ──
  const effectiveSlider = variant === "se" ? SE_SPEED[seSpeed] : slider;
  const current = useMemo(
    () =>
      enabled
        ? mergeSources([touchState, keys, pad], effectiveSlider, variant, lastAxis.current)
        : { ...NEUTRAL, slider: effectiveSlider },
    [enabled, touchState, keys, pad, effectiveSlider, variant],
  );

  useEffect(() => {
    sender.push(current);
  }, [sender, current]);

  // 꺼지면 손가락 기록도 지운다(다음 판에 눌린 채로 시작하지 않게)
  useEffect(() => {
    if (!enabled) setTouch(new Map());
  }, [enabled]);

  const held = useMemo(() => {
    const s = new Set<TouchControl>();
    if (current.dpad) s.add(current.dpad);
    if (current.a) s.add("a");
    if (current.b) s.add("b");
    return s;
  }, [current]);

  return {
    current,
    slider,
    setSlider,
    seSpeed,
    setSeSpeed,
    press,
    release,
    held,
    gamepad,
    stats: sender.stats,
  };
}
